// src/services/authService.js
import axios from 'axios';

// Detect if accessing from network IP and adjust API URL
const getAPIURL = () => {
  const hostname = window.location.hostname;

  // Localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  }

  // Local network testing (mobile on LAN) — private IP ranges only
  if (/^(192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(hostname)) {
    return `http://${hostname}:3001/api`;
  }

  // Production (same origin, no port, matches page protocol)
  return `${window.location.protocol}//${hostname}/api`;
};

const API_URL = getAPIURL();

// ============================================================================
// SERVICE ERRORS THAT KEEP WHAT THE SERVER SAID
// ----------------------------------------------------------------------------
// Every method in this file rethrows `new Error(response?.data?.message)`. That
// preserves the sentence and throws away everything else — the status code, and
// any structured flag the server set. A caller can then only string-match the
// prose, which breaks the moment someone rewords a message.
//
// The concrete case this was written for: /auth/login answers a verified account
// with no company with a 403 carrying { registrationIncomplete: true,
// resumeMember: {...} }. The flag and the identity were both being discarded
// here, so LoginForm could only detect the condition by searching the message
// text, and had no way at all to recover the memberId needed to finish the
// registration.
//
// BACKWARD COMPATIBLE BY CONSTRUCTION: the message is computed exactly as
// before, so `err.message` comparisons elsewhere keep working unchanged. This
// only ADDS `err.status` and `err.data`.
//
// APPLIED TO login() AND register() — the two methods whose error responses
// carry a flag the server set on purpose:
//   login()     403 registrationIncomplete + resumeMember, and 401 emailNotVerified
//   register()  400 verificationRequired
//
// The other seventeen methods in this file still use the old plain throw, and
// that is not an oversight: every one of their error responses is
// { success: false, message }, so the only thing their catch discards is the
// HTTP status. Nothing reads it. Converting them is a one-line change each and
// should happen when something actually needs a status or a flag, not as a
// sweep that touches seventeen call paths to change nothing.
const asServiceError = (error, fallbackMessage) => {
  const data = (error && error.response && error.response.data) || null;
  const err = new Error((data && data.message) || fallbackMessage);
  err.status = (error && error.response && error.response.status) || null;
  err.data = data;
  return err;
};

const authService = {
  // ============================================================================
  // AUTHENTICATION METHODS
  // ============================================================================

  // Register a new user
  //
  // On failure throws an Error whose `message` is what it always was, plus
  // `status` and `data` from the server.
  //
  // verificationRequired is mirrored onto the error for the same reason
  // registrationIncomplete is on login: the server sets it deliberately
  // (authController returns 400 { verificationRequired: true } when the email
  // was never proven, or when the proof has aged out of its grace window), and
  // it is the one registration failure a caller would want to handle
  // differently — send them back to the code entry rather than showing a form
  // error. Reading it off the error beats matching the sentence.
  register: async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        ...userData,
        // Explicitly ensure these are included
        userAgreementAccepted: userData.userAgreementAccepted,
        cookieConsent: userData.cookieConsent
      }, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      const err = asServiceError(error, 'Registration failed');
      if (err.data && err.data.verificationRequired) err.verificationRequired = true;
      throw err;
    }
  },

  // Login user
  //
  // On failure throws an Error whose `message` is what it always was, plus
  // `status` and `data` from the server. See asServiceError above.
  //
  // The registrationIncomplete case is mirrored onto the error itself because
  // it is the one branch a caller acts on: LoginForm tests
  // `err.registrationIncomplete` first and only falls back to reading the
  // message text. Promoting it here means that first test now succeeds, and the
  // prose-matching fallback becomes what it should be — a safety net rather
  // than the mechanism.
  //
  // resumeMember is identity ONLY (memberId, email, first/last name) and comes
  // with no session — see authController's note. It exists so the sign-in form
  // can hand the person to /register?resume=true without making them re-prove
  // who they are.
  login: async (email, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      const err = asServiceError(error, 'Login failed');
      if (err.data && err.data.registrationIncomplete) {
        err.registrationIncomplete = true;
        err.resumeMember = err.data.resumeMember || null;
      }
      throw err;
    }
  },

  // Logout user
  logout: async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/logout`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Logout failed');
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get user data');
    }
  },

  // Update logout settings
  updateLogoutSettings: async (automaticLogoutDurationMinutes, lessThanDayLogout) => {
    try {
      const response = await axios.put(
        `${API_URL}/auth/logout-settings`,
        { automaticLogoutDurationMinutes, lessThanDayLogout },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update logout settings');
    }
  },

  // Refresh token
  refreshToken: async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/refresh-token`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to refresh token');
    }
  },

  // ============================================================================
  // EMAIL VERIFICATION METHODS
  // ============================================================================

  // Send verification code to email
  sendVerificationCode: async (email) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/send-verification-code`,
        { email },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send verification code');
    }
  },

  // Verify email with code
  verifyEmailCode: async (email, code) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/verify-email`,
        { email, code },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Email verification failed');
    }
  },

  // Resend verification code
  resendVerificationCode: async (email) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/resend-verification-code`,
        { email },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to resend verification code');
    }
  },

  // Update email verified status (after successful verification)
  updateEmailVerified: async (email) => {
    try {
      const response = await axios.put(
        `${API_URL}/auth/update-email-verified`,
        { email },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update email verification status');
    }
  },

  // ============================================================================
  // ACCOUNT DELETION METHOD
  // ============================================================================

  // Delete personal account (soft delete)
  deletePersonalAccount: async (email) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/delete-personal-account`,
        { email },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete account');
    }
  },

  // ============================================================================
  // PASSWORD RESET METHODS
  // ============================================================================

  // Send password reset code to email
  sendPasswordResetCode: async (email) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/send-password-reset-code`,
        { email },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send password reset code');
    }
  },

  // Verify password reset code
  verifyPasswordResetCode: async (email, code) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/verify-password-reset-code`,
        { email, code },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Password reset code verification failed');
    }
  },

  // Reset password with verified code
  resetPassword: async (email, newPassword, code) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/reset-password`,
        { email, newPassword, code },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  },

  // ============================================================================
  // UNVERIFIED ACCOUNT DELETION METHOD
  // ============================================================================

  // Delete unverified account (for timer expiration or page exit)
  deleteUnverifiedAccount: async (memberId) => {
    try {
      const response = await axios.delete(
        `${API_URL}/auth/delete-unverified/${memberId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete unverified account');
    }
  },

  // ============================================================================
  // IMPERSONATION METHODS (Super Admin only)
  // ============================================================================

  // Search members for impersonation
  searchMembersForImpersonation: async (searchQuery) => {
    try {
      const response = await axios.get(
        `${API_URL}/auth/impersonation/search`,
        { 
          params: { search: searchQuery },
          withCredentials: true 
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search members');
    }
  },

  // Get current impersonation status
  getImpersonationStatus: async () => {
    try {
      const response = await axios.get(
        `${API_URL}/auth/impersonation/status`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get impersonation status');
    }
  },

  // Start impersonating a user
  startImpersonation: async (memberId) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/impersonate/${memberId}`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to start impersonation');
    }
  },

  // Stop impersonating and return to admin account
  stopImpersonation: async () => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/stop-impersonation`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to stop impersonation');
    }
  }
};

export default authService;